import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  PermIdentity as PermIdentityIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import { toTitleCase } from '../../../utils/formatters/text';
import { PhoneNumberInput } from '../../../components/ui';
import { salesNestedDialogSx } from '../shared/salesModalStack';
import type { QuickCustomerDialogMode } from './SalesStep3Customer.hooks';

export interface QuickCustomerDialogProps {
  open: boolean;
  mode: QuickCustomerDialogMode;
  quickName: string;
  quickCi: string;
  quickPhone: string;
  quickNameError: string | null;
  quickCiError: string | null;
  quickFormError: string | null;
  isQuickSaving: boolean;
  createAcknowledged: boolean;
  onCreateAcknowledgedChange: (value: boolean) => void;
  onClose: () => void;
  onNameChange: (value: string) => void;
  onCiChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onSubmit: () => void;
}

const QuickCustomerDialog: React.FC<QuickCustomerDialogProps> = ({
  open,
  mode,
  quickName,
  quickCi,
  quickPhone,
  quickNameError,
  quickCiError,
  quickFormError,
  isQuickSaving,
  createAcknowledged,
  onCreateAcknowledgedChange,
  onClose,
  onNameChange,
  onCiChange,
  onPhoneChange,
  onSubmit,
}) => {
  const isCreate = mode === 'create';
  const canSave = !isQuickSaving && (!isCreate || createAcknowledged);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isQuickSaving}
      sx={salesNestedDialogSx}
      PaperProps={{
        sx: (theme) => ({
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.4 : 0.25)}`,
        }),
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          background: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 45%, #6366f1 100%)',
          color: '#ffffff',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
          {mode === 'edit' ? 'Editar cliente' : 'Registro rápido de cliente'}
        </Typography>
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          disabled={isQuickSaving}
          size="small"
          sx={{
            color: 'inherit',
            '&:hover': { backgroundColor: alpha('#ffffff', 0.12) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            required
            fullWidth
            size="medium"
            label="Nombre completo"
            value={quickName}
            onChange={(e) => onNameChange(e.target.value)}
            onBlur={(e) => onNameChange(toTitleCase(e.target.value))}
            error={Boolean(quickNameError)}
            helperText={quickNameError}
            disabled={isQuickSaving}
            placeholder="Ej. María Fernanda Rojas"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
              sx: (theme) => ({
                borderRadius: 2.5,
                backgroundColor: theme.palette.background.paper,
              }),
            }}
            sx={(theme) => ({
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.45 : 0.35,
                ),
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.success.main, 0.55),
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.success.main,
              },
            })}
          />
          <TextField
            fullWidth
            size="medium"
            label="Cédula de identidad"
            value={quickCi}
            onChange={(e) => onCiChange(e.target.value)}
            error={Boolean(quickCiError)}
            helperText={quickCiError ?? 'Opcional. Si se ingresa, debe ser única en el sistema.'}
            FormHelperTextProps={{
              sx: quickCiError ? undefined : { color: 'text.secondary' },
            }}
            disabled={isQuickSaving}
            placeholder="Ej. 1234567"
            inputProps={{ maxLength: 32 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PermIdentityIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
              sx: (theme) => ({
                borderRadius: 2.5,
                backgroundColor: theme.palette.background.paper,
              }),
            }}
            sx={(theme) => ({
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.45 : 0.35,
                ),
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.success.main, 0.55),
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.success.main,
              },
            })}
          />
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Teléfono
            </Typography>
            <PhoneNumberInput
              value={quickPhone}
              onChange={onPhoneChange}
              disabled={isQuickSaving}
              nationalNumberLabel="Número"
              nationalNumberPlaceholder="Ej. 71234567"
              textFieldProps={{
                size: 'medium',
                sx: (theme) => ({
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    backgroundColor: theme.palette.background.paper,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(
                      theme.palette.success.main,
                      theme.palette.mode === 'dark' ? 0.45 : 0.35,
                    ),
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.success.main, 0.55),
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.success.main,
                  },
                }),
              }}
            />
          </Box>
          {isCreate ? (
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
                mx: 0,
                gap: 0.5,
                '& .MuiFormControlLabel-label': { pt: 0.25 },
              }}
              control={
                <Checkbox
                  checked={createAcknowledged}
                  onChange={(e) => onCreateAcknowledgedChange(e.target.checked)}
                  disabled={isQuickSaving}
                  color="success"
                  sx={{ pt: 0 }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  Entiendo que el cliente se guardará en el sistema aunque cancele esta venta.
                </Typography>
              }
            />
          ) : null}
        </Box>
        {quickFormError != null ? (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {quickFormError}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 0,
          gap: 1.25,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="tonal"
          onClick={onClose}
          disabled={isQuickSaving}
          sx={{
            borderRadius: 2.5,
            fontWeight: 700,
            px: 2.5,
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void onSubmit()}
          disabled={!canSave}
          sx={{
            borderRadius: 2.5,
            fontWeight: 700,
            px: 2.5,
            background: 'linear-gradient(to right, #06b6d4, #10b981)',
            boxShadow: '0 14px 20px -16px rgba(6, 182, 212, 0.95)',
            '&:hover': {
              background: 'linear-gradient(to right, #0891b2, #059669)',
            },
          }}
        >
          {isQuickSaving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickCustomerDialog;
